import { Box, Divider, HStack, Stack, Text } from '@chakra-ui/react';
import { Comment, HeartFilled } from '@/client/components/icons/ContinuIcons';

import ErrorAlert from '@/client/components/data-display/ErrorAlert';
import Loading from '@/client/components/media/Loading';
import User from '@/client/services/api/User';
import type { User as UserType } from '@/client/types/User';
import { useAuthStore } from '@/client/services/state/authStore';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

interface LearnerProfileActivityFeedProps {
  userData: UserType;
}

export default function LearnerProfileActivityFeed({ userData }: LearnerProfileActivityFeedProps) {
  const { t } = useTranslation();
  const { id } = useParams();
  const { authConfig } = useAuthStore();
  const { user } = authConfig;

  const userId = id || user._id;

  const { first_name: firstName } = userData;

  const {
    data: feedData,
    isLoading: isFeedLoading,
    isError: isFeedError,
  } = useQuery({
    queryKey: ['query-user-activity-feed', userId],
    queryFn: async () => User.getUserActivityFeed(userId),
  });

  const contentTypeMap: { [key: string]: string } = {
    article: t('activityFeed.the_article'),
    video: t('activityFeed.the_video'),
    file: t('activityFeed.the_file'),
    track: t('activityFeed.the_track'),
    presentation: t('activityFeed.the_presentation'),
    'scorm-content': t('activityFeed.the_scorm_content'),
    imported_content: t('activityFeed.the_vendor'),
    event: t('activityFeed.the_event'),
    assessment: t('activityFeed.the_assessment'),
    quiz: t('activityFeed.the_quiz'),
  };

  const actionTypeMap: { [key: string]: string } = {
    like: t('activityFeed.liked'),
    comment: t('activityFeed.commented_on'),
  };

  const iconMap: { [key: string]: React.ReactNode } = {
    like: <HeartFilled color="red.400" />,
    comment: <Comment color="brand.primary" />,
  };

  if (isFeedLoading) return <Loading />;

  if (isFeedError) return <ErrorAlert title={t('learners.profile.activityFeed.error')} />;

  return (
    <Stack spacing={6}>
      <Stack spacing={2}>
        <Text fontSize="18px" fontWeight={600} color="baseBlack">
          {t('learners.profile.activityFeed.title')}
        </Text>

        <HStack spacing={4}>
          <HStack spacing={2}>
            <Box
              width="fit-content"
              paddingY={1}
              paddingX={2}
              backgroundColor="warmNeutral.300"
              borderRadius="full"
            >
              <Text fontSize="10px" fontWeight={600} color="baseBlack">
                {feedData?.userCommentCount || 0}
              </Text>
            </Box>

            <Text fontSize="14px" fontWeight={500} color="baseBlack">
              {t('learners.profile.activityFeed.comments')}
            </Text>
          </HStack>

          <HStack spacing={2}>
            <Box
              width="fit-content"
              paddingY={1}
              paddingX={2}
              backgroundColor="warmNeutral.300"
              borderRadius="full"
            >
              <Text fontSize="10px" fontWeight={600} color="baseBlack">
                {feedData?.userContentViews || 0}
              </Text>
            </Box>

            <Text fontSize="14px" fontWeight={500} color="baseBlack">
              {t('learners.profile.activityFeed.contentViews')}
            </Text>
          </HStack>
        </HStack>
      </Stack>

      <Divider />

      <Stack spacing={8}>
        {feedData?.activityFeed.map((item) => (
          <HStack
            spacing={2}
            key={`${item._id}-${item.actionType}-${item.createdAt}`}
            alignItems="flex-start"
          >
            <Box>{iconMap[item.actionType]}</Box>

            <Stack spacing={2} paddingTop={0}>
              <Text fontSize="14px" fontWeight={500} color="baseBlack">
                {`${firstName}  ${actionTypeMap[item.actionType]} ${contentTypeMap[item.type]}, ${
                  item.title
                }`}
              </Text>

              <Text fontSize="12px" fontWeight={500} color="neutral.1000">
                {t('activityFeed.timeSince', {
                  time: item.timeSince,
                })}
              </Text>
            </Stack>
          </HStack>
        ))}
      </Stack>
    </Stack>
  );
}
